<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import AppointmentPresenter from '@satellite/components/elements/presenters/AppointmentPresenter.vue';
import YardAssetVisitPresenter from '@satellite/components/elements/presenters/YardAssetVisitPresenter.vue';
import CompanyPresenterV2 from '@satellite/components/elements/presenters/CompanyPresenterV2.vue';
import PresenterValueFallback from '@satellite/components/elements/presenters/PresenterValueFallback.vue';
import YardSpotPresenterV2 from '@satellite/components/elements/presenters/YardSpotPresenterV2.vue';
import YardAssetStateIcon from '@satellite/components/elements/presenters/yard/YardAssetStateIcon.vue';

import type { PresenterDisplaySize } from '@satellite/composables/presenterDisplayOptions';
import type {
  YardAssetDisplayField,
  YardAssetDisplayData
} from '@satellite/composables/useYardAssetDisplay';

const {
  data,
  size,
  orientation = 'horizontal',
  displayFields = ['code']
} = defineProps<{
  data: YardAssetDisplayData;
  size?: PresenterDisplaySize;
  orientation?: 'horizontal' | 'vertical';
  displayFields?: YardAssetDisplayField[];
}>();

const resolvedSize = computed(() => size ?? data.size);

const { t } = useI18n();

const showCode = computed(() => displayFields.includes('code'));
const showType = computed(() => displayFields.includes('type'));
const showTypeChip = computed(() => displayFields.includes('typeChip'));
const showTypeIcon = computed(() => displayFields.includes('typeIcon'));
const showOwner = computed(() => displayFields.includes('owner'));
const showSpot = computed(() => displayFields.includes('spot'));
const showArrivedTime = computed(() => displayFields.includes('arrivedTime'));
const showStatus = computed(() => displayFields.includes('status'));
const showStatusChip = computed(() => displayFields.includes('statusChip'));
const showStatusIcon = computed(() => displayFields.includes('statusIcon'));
const showDisplayStatus = computed(() => displayFields.includes('displayStatus'));
const showDisplayStatusChip = computed(() => displayFields.includes('displayStatusChip'));
const showDisplayStatusIcon = computed(() => displayFields.includes('displayStatusIcon'));
const showState = computed(() => displayFields.includes('state'));
const showStateChip = computed(() => displayFields.includes('stateChip'));
const showTags = computed(() => displayFields.includes('tags'));
const showStateIcon = computed(() => displayFields.includes('stateIcon'));
const showDropoffCarrierCompany = computed(() => displayFields.includes('dropoffCarrierCompany'));
const showPickupCarrierCompany = computed(() => displayFields.includes('pickupCarrierCompany'));
</script>

<template>
  <span
    class="d-flex yard-asset-display-v2"
    v-bind="$attrs"
    :class="{
      'flex-row align-center gap-1': orientation === 'horizontal',
      'flex-column align-start gap-0-5': orientation === 'vertical',
      'typography-body-sm': resolvedSize === 'small',
      'typography-body-md': resolvedSize === 'regular',
      'typography-heading-lg-bold': resolvedSize === 'large'
    }">
    <template v-if="showStateIcon">
      <span class="d-flex flex-row gap-1 align-center">
        <yard-asset-state-icon
          v-bind="data.stateIconAttrs"
          :asset="data.asset"
          :orientation="orientation"
          :loading="data.isFetching" />
      </span>
    </template>

    <template v-if="showCode">
      <slot
        name="asset-code"
        :asset-code="data.code"
        :asset-code-attrs="data.codeAttrs({ size: resolvedSize })">
        <span v-bind="data.codeAttrs({ size: resolvedSize })">
          {{ data.code }}
        </span>
      </slot>
    </template>

    <template v-if="showType">
      <slot name="asset-type" :asset-type="data.typeRaw" :asset-type-attrs="data.typeAttrs">
        <span v-bind="data.typeAttrs">
          {{ data.typeLabel }}
        </span>
      </slot>
    </template>

    <template v-if="showTypeChip">
      <v-chip v-bind="data.typeChipAttrs({ size: resolvedSize })">
        <slot
          name="asset-type-chip"
          :asset-type="data.typeRaw"
          :asset-translated-type="data.typeLabel"
          :asset-type-chip-text-attrs="data.typeChipTextAttrs({ size: resolvedSize })">
          <span v-bind="data.typeChipTextAttrs({ size: resolvedSize })">
            {{ data.typeLabel }}
          </span>
        </slot>
      </v-chip>
    </template>

    <template v-if="showTypeIcon">
      <slot name="asset-type-icon">
        <v-icon v-bind="data.typeIconAttrs" />
      </slot>
    </template>

    <template v-if="showOwner">
      <company-presenter-v2
        v-if="data.owner?.id"
        :company-id="data.owner.id"
        :display-fields="['name']"
        :mode="data.mode"
        :size="resolvedSize"
        v-bind="data.ownerPresenterProps">
        <template #company-name="{ companyName, companyNameAttrs }">
          <slot
            name="asset-owner"
            :asset-owner="data.owner"
            :owner-company-name="companyName"
            :owner-company-name-attrs="companyNameAttrs">
            <span v-bind="companyNameAttrs">
              {{ companyName }}
            </span>
          </slot>
        </template>
      </company-presenter-v2>
      <presenter-value-fallback v-else data-testid="yard-asset-presenter-no-information-available">
        <template #default>
          {{ t('core.yard.messages.unknownCompany') }}
        </template>
      </presenter-value-fallback>
    </template>

    <template v-if="showSpot">
      <span class="d-flex flex-row gap-1 align-center">
        <v-icon :icon="data.spotIcon" size="16" />

        <template v-if="data.isInTransit">
          <slot name="in-transit">
            <presenter-value-fallback data-testid="yard-asset-presenter-in-transit">
              <template #default>
                {{ t('core.yard.assetContainerEvent.types.inTransit') }}
              </template>
            </presenter-value-fallback>
          </slot>
        </template>

        <template v-else-if="data.hasAssignment">
          <slot name="spot" :spot-id="data.spotId">
            <yard-spot-presenter-v2
              :display-fields="['area', 'code']"
              :spot-id="data.spotId"
              :size="resolvedSize"
              :mode="data.mode"
              v-bind="data.spotPresenterProps" />
          </slot>
        </template>

        <template v-else>
          <slot name="waiting-assignment">
            <presenter-value-fallback data-testid="yard-asset-presenter-waiting-assignment">
              <template #default>
                {{ t('core.yard.messages.waitingAssignment') }}
              </template>
            </presenter-value-fallback>
          </slot>
        </template>
      </span>
    </template>

    <template v-if="showArrivedTime">
      <slot
        name="arrived-time"
        :arrived-time="data.arrivedTime"
        :arrived-time-formatted="data.arrivedTimeFormatted"
        :arrived-time-attrs="data.arrivedTimeAttrs">
        <presenter-value-fallback v-if="!data.arrivedTime" />
        <span v-else v-bind="data.arrivedTimeAttrs">
          {{ data.arrivedTimeFormatted }}
        </span>
      </slot>
    </template>

    <template v-if="showStatus">
      <slot name="asset-status" :asset-status="data.status" :asset-status-attrs="data.statusAttrs">
        <presenter-value-fallback v-if="!data.status" />
        <span v-else v-bind="data.statusAttrs">
          {{ data.statusLabel }}
        </span>
      </slot>
    </template>

    <template v-if="showStatusChip">
      <v-chip v-if="data.status" v-bind="data.statusChipAttrs({ size: resolvedSize })">
        <slot
          name="asset-status-chip"
          :asset-status-chip="data.status"
          :asset-status-chip-text-attrs="data.statusChipTextAttrs({ size: resolvedSize })">
          <span v-bind="data.statusChipTextAttrs({ size: resolvedSize })">
            {{ data.statusLabel }}
          </span>
        </slot>
      </v-chip>
      <presenter-value-fallback v-else />
    </template>

    <template v-if="showStatusIcon">
      <slot name="asset-status-icon" :asset-status-icon="data.statusIcon">
        <v-icon v-bind="data.statusIconAttrs" />
      </slot>
    </template>

    <template v-if="showDisplayStatus">
      <slot
        name="asset-display-status"
        :asset-display-status="data.displayStatus"
        :asset-display-status-attrs="data.displayStatusAttrs">
        <presenter-value-fallback v-if="!data.displayStatus" />
        <span v-else v-bind="data.displayStatusAttrs">
          {{ data.displayStatusLabel }}
        </span>
      </slot>
    </template>

    <template v-if="showDisplayStatusChip">
      <v-chip
        v-if="data.displayStatus"
        v-bind="data.displayStatusChipAttrs({ size: resolvedSize })">
        <slot
          name="asset-display-status-chip"
          :asset-display-status-chip="data.displayStatus"
          :asset-display-status-chip-text-attrs="
            data.displayStatusChipTextAttrs({ size: resolvedSize })
          ">
          <span v-bind="data.displayStatusChipTextAttrs({ size: resolvedSize })">
            {{ data.displayStatusLabel }}
          </span>
        </slot>
      </v-chip>
      <presenter-value-fallback v-else />
    </template>

    <template v-if="showDisplayStatusIcon">
      <slot name="asset-display-status-icon" :asset-display-status-icon="data.displayStatusIcon">
        <v-icon v-bind="data.displayStatusIconAttrs" />
      </slot>
    </template>

    <template v-if="showState">
      <slot name="asset-state" :asset-state="data.state" :asset-state-attrs="data.stateAttrs">
        <presenter-value-fallback v-if="!data.state" />
        <span v-else v-bind="data.stateAttrs">
          {{ data.stateLabel }}
        </span>
      </slot>
    </template>

    <template v-if="showStateChip">
      <v-chip v-bind="data.stateChipAttrs({ size: resolvedSize })">
        <slot
          name="asset-state-chip"
          :asset-state-chip="data.state"
          :asset-state-chip-text-attrs="data.stateChipTextAttrs({ size: resolvedSize })">
          <span v-bind="data.stateChipTextAttrs({ size: resolvedSize })">
            {{ data.stateLabel || t('core.yard.messages.notInformed') }}
          </span>
        </slot>
      </v-chip>
    </template>

    <template v-if="showTags">
      <slot name="tags" :tags="data.tags">
        <div v-if="Array.isArray(data.tags) && data.tags.length" v-bind="data.tagsContainerAttrs">
          <v-chip
            v-for="(tag, tagIndex) in data.tags"
            :key="tag"
            :size="resolvedSize === 'small' ? 'x-small' : 'small'"
            variant="outlined">
            <slot
              name="tag-chip"
              :tag="tag"
              :index="tagIndex"
              :tag-chip-text-attrs="data.tagChipAttrs({ size: resolvedSize })">
              <span v-bind="data.tagChipAttrs({ size: resolvedSize })">
                {{ tag }}
              </span>
            </slot>
          </v-chip>
        </div>
        <presenter-value-fallback v-else />
      </slot>
    </template>

    <template v-if="showDropoffCarrierCompany">
      <appointment-presenter
        v-if="data.dropoffCarrier?.appointmentId"
        :appointment-id="data.dropoffCarrier.appointmentId"
        v-bind="data.dropoffAppointmentPresenterProps"
        :display-fields="['carrierCompany']"
        :mode="data.mode"
        :size="resolvedSize">
        <template #carrier-company="{ carrierCompany, carrierCompanyAttrs }">
          <slot
            name="dropoff-carrier-company"
            :dropoff-carrier-company="carrierCompany"
            :dropoff-carrier-company-attrs="carrierCompanyAttrs">
            <span v-bind="carrierCompanyAttrs">
              {{ carrierCompany }}
            </span>
          </slot>
        </template>
      </appointment-presenter>
      <yard-asset-visit-presenter
        v-else-if="data.dropoffCarrier?.assetVisitId"
        :asset-visit-id="data.dropoffCarrier.assetVisitId"
        v-bind="data.dropoffAssetVisitPresenterProps"
        :display-fields="['companyName']"
        :mode="data.mode"
        :size="resolvedSize">
        <template #company-name="{ companyName, companyNameAttrs }">
          <slot
            name="dropoff-carrier-company"
            :dropoff-carrier-company="companyName"
            :dropoff-carrier-company-attrs="companyNameAttrs">
            <span v-bind="companyNameAttrs">
              {{ companyName }}
            </span>
          </slot>
        </template>
      </yard-asset-visit-presenter>
      <presenter-value-fallback v-else />
    </template>

    <template v-if="showPickupCarrierCompany">
      <appointment-presenter
        v-if="data.pickupCarrier?.appointmentId"
        :appointment-id="data.pickupCarrier.appointmentId"
        v-bind="data.pickupAppointmentPresenterProps"
        :display-fields="['carrierCompany']"
        :mode="data.mode"
        :size="resolvedSize">
        <template #carrier-company="{ carrierCompany, carrierCompanyAttrs }">
          <slot
            name="pickup-carrier-company"
            :pickup-carrier-company="carrierCompany"
            :pickup-carrier-company-attrs="carrierCompanyAttrs">
            <span v-bind="carrierCompanyAttrs">
              {{ carrierCompany }}
            </span>
          </slot>
        </template>
      </appointment-presenter>
      <yard-asset-visit-presenter
        v-else-if="data.pickupCarrier?.assetVisitId"
        :asset-visit-id="data.pickupCarrier.assetVisitId"
        v-bind="data.pickupAssetVisitPresenterProps"
        :display-fields="['companyName']"
        :mode="data.mode"
        :size="resolvedSize">
        <template #company-name="{ companyName, companyNameAttrs }">
          <slot
            name="pickup-carrier-company"
            :pickup-carrier-company="companyName"
            :pickup-carrier-company-attrs="companyNameAttrs">
            <span v-bind="companyNameAttrs">
              {{ companyName }}
            </span>
          </slot>
        </template>
      </yard-asset-visit-presenter>
      <presenter-value-fallback v-else />
    </template>

    <slot name="default" :asset="data.asset" :spot="data.spot" :spot-id="data.spotId"></slot>
  </span>
</template>
